import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material';
import { ApiError, getSessionInfo } from '../api';
import { redirectPath } from '../redirects';
import { REDIRECT_TO_PATH_KEY, signInWith, signOut, type Provider } from '../supabase';
import { useSession } from '../useSession';

// /auth?redirectToPath=<path>: the sign-in page, also where Supabase sends the user back after the provider.
export const Login: React.FC = () => {
    const navigate = useNavigate();
    const session = useSession();
    const [search] = useState(() => new URLSearchParams(window.location.search));
    const [error, setError] = useState<string | null>(search.get('error_description') ?? search.get('error'));
    const [busy, setBusy] = useState(false);
    const redirectToPath = redirectPath(search.get('redirectToPath') ?? sessionStorage.getItem(REDIRECT_TO_PATH_KEY));

    useEffect(() => {
        if (!session) return;
        // Signs the user in to the identity backend (creates/links the identity user, refuses accounts that may not
        // sign in) before leaving. provider_token is only on the session right after the provider redirect.
        getSessionInfo(session.provider_token ?? undefined)
            .then(() => {
                sessionStorage.removeItem(REDIRECT_TO_PATH_KEY);
                navigate(redirectToPath, { replace: true });
            })
            .catch(async (e: Error) => {
                if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
                    await signOut();
                }
                setError(e.message);
            });
    }, [session]);

    async function signIn (provider: Provider) {
        setBusy(true);
        setError(null);
        try {
            await signInWith(provider, redirectToPath);
        } catch (e) {
            setError((e as Error).message);
            setBusy(false);
        }
    }

    if (session === undefined || (session && !error)) {
        return <Box className="center"><CircularProgress /></Box>;
    }

    return (
        <Box className="center">
            <Card sx={{ width: 420, maxWidth: '100%' }}>
                <CardContent>
                    <Stack spacing={2} alignItems="stretch">
                        <Box sx={{ textAlign: 'center' }}>
                            <span className="brand">
                                <img src="/favicon.svg" alt="" width={44} height={44} />
                                <span className="brand-name">GoodDev</span>
                            </span>
                        </Box>
                        <Typography variant="h5" align="center">Sign in</Typography>
                        {error && <Alert severity="error">{error}</Alert>}
                        <Button variant="contained" size="large" disabled={busy} onClick={() => signIn('azure')}>
                            Continue with Microsoft/Devsoft Accounts
                        </Button>
                        <Button variant="outlined" size="large" disabled={busy} onClick={() => signIn('google')}>
                            Continue with Google
                        </Button>
                        <Typography variant="body2" color="text.secondary" align="center">
                            Devsoft employees sign in with their Microsoft account. Other users sign in with their
                            Google Workspace account.
                        </Typography>
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
};
